import {
  faBroomWide,
  faCopy,
  faDownload,
  faPaste,
  faUpload
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { enqueueNotification } from 'Utils/Helpers'
import { JSONPrettyView, LabelContent } from 'V2Components'
import {
  FileUpload,
  GCP_CLOUD_ACCOUNT_FOLDER_PERMISSIONS,
  GCP_CLOUD_ACCOUNT_ORG_PERMISSIONS,
  GCP_CLOUD_ACCOUNT_PROJECT_PERMISSIONS,
  useAddAccountWizardContext
} from 'features/clouds'
import { GCP_WORKFORCE_SETUP_URL_MAP } from 'features/clouds/utils/gcp'
import { getAppEnvironment, getHTMLDataTestID } from 'infra/utils'
import { IconButton, Label, RadioInput, TargetIcon, TextInput, Typography } from 'procyon-ui'
import React from 'react'

const env = getAppEnvironment()

/**
 * @returns
 */
const GCPAccountTypeStep = () => {
  const { gcpSpec, setGcpSpec, isEditMode } = useAddAccountWizardContext()

  return (
    <div>
      {!isEditMode && (
        <>
          <Typography variant='body-regular'>
            Select the type of GCP Account you are going to add.
          </Typography>
          <div className='flex flex-col gap-2 mt-2 mb-4'>
            <RadioInput
              data-testid={getHTMLDataTestID('clouds-gcp-add', 'input', 'is-org')}
              checked={gcpSpec.accountType === 'ORG'}
              // @ts-ignore
              label={
                <div className='flex gap-1 items-center'>
                  <TargetIcon type='GCP_ORG' />
                  <Typography variant='body-regular'>Organization</Typography>
                </div>
              }
              onChange={() => setGcpSpec({ accountType: 'ORG' })}
              value='ORG'
            />
            <RadioInput
              data-testid={getHTMLDataTestID('clouds-gcp-add', 'input', 'is-folder')}
              checked={gcpSpec.accountType === 'FOLDER'}
              // @ts-ignore
              label={
                <div className='flex gap-1 items-center'>
                  <TargetIcon type='GCP_GOOGLE_FOLDER' />
                  <Typography variant='body-regular'>Folder</Typography>
                </div>
              }
              onChange={() => setGcpSpec({ accountType: 'FOLDER' })}
              value='folder'
            />
            <RadioInput
              data-testid={getHTMLDataTestID('clouds-gcp-add', 'input', 'is-project')}
              checked={gcpSpec.accountType === 'PROJECT'}
              // @ts-ignore
              label={
                <div className='flex gap-1 items-center'>
                  <TargetIcon type='GCP_GOOGLE_PROJECT' />
                  <Typography variant='body-regular'>Project</Typography>
                </div>
              }
              onChange={() => setGcpSpec({ accountType: 'PROJECT' })}
              value='PROJECT'
            />
          </div>
        </>
      )}
      {/* {!isEditMode && (
        <LabelCheckbox
          name='Use Workforce Federation'
          checked={isWFFedAccount}
          onChange={() => {
            if (gcpSpec.cloudIDType === 'intermediate') setGcpSpec({ cloudIDType: 'workforce-fed' })
            else setGcpSpec({ cloudIDType: 'intermediate' })
          }}
        />
      )} */}
    </div>
  )
}

const WFedStep = () => {
  const { gcpSpec, setGcpSpec } = useAddAccountWizardContext()

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text)
      enqueueNotification('Command copied to clipboard', 'info')
    } catch (err) {
      enqueueNotification('Failed to copy command!', 'error')
    }
  }

  const textToCopy = `wget ${GCP_WORKFORCE_SETUP_URL_MAP[env]}`

  const GCPWFProviderIDSetupSteps = [
    {
      content: (
        <>
          Perform gcloud auth login using user which has{' '}
          <code className='bg-gray-200 p-[2px] rounded-sm'>roles/iam.workforcePoolAdmin</code>{' '}
          permission at Org level.
        </>
      )
    },
    {
      content: (
        <div>
          <a
            download='workforce.sh'
            className='text-blue-500 mr-1'
            href={GCP_WORKFORCE_SETUP_URL_MAP[env]}
          >
            <FontAwesomeIcon className='mr-[2px]' icon={faDownload} />
            Download
          </a>
          shell script
          <div className='flex items-center gap-2 mt-2 mr-4'>
            <code className='bg-gray-200 p-1 rounded-sm overflow-auto block whitespace-nowrap flex-1'>
              {textToCopy}
            </code>
            <IconButton
              icon={faCopy}
              variant='primary'
              title='Copy command'
              onClick={() => handleCopy(textToCopy)}
            />
          </div>
        </div>
      )
    },
    {
      content: (
        <>
          Run the shell script{' '}
          <code className='bg-gray-200 p-[2px] rounded-sm ml-1'>sh workforce.sh</code>
        </>
      )
    },
    {
      content: 'Copy the configured GCP Provider ID and paste it below.'
    }
  ]

  return (
    <div>
      <Typography variant='body-regular'>
        To use Workforce Identity Federation, you need to create a Provider ID in GCP Console.
        <br />
      </Typography>
      <Typography variant='body-regular'>Follow the steps to create one:</Typography>
      <div>
        <Typography className='mt-2' variant='body-regular'>
          <ol>
            {GCPWFProviderIDSetupSteps.map((e, i) => {
              return (
                <li className='flex items-start gap-2 mb-1' key={i}>
                  <Typography variant='body-regular'>{i + 1}.</Typography>
                  <Typography variant='body-regular'>{e.content}</Typography>
                </li>
              )
            })}
          </ol>
        </Typography>
      </div>
      <div className='border-t-2 -mx-5 my-1 mt-2'></div>
      <Typography className='!text-[#545b71] mb-1' variant='buttonLabel-regular'>
        Provider ID
      </Typography>
      <TextInput
        data-testid={getHTMLDataTestID('clouds-gcp-add', 'input', 'primary-domain')}
        sx={{ width: '100%' }}
        value={gcpSpec.primaryDomain}
        onChange={(e) => setGcpSpec({ primaryDomain: e.target.value })}
      />
    </div>
  )
}

const DomainRestrictionsStep = () => {
  return (
    <div>
      <Typography variant='body-regular'>
        Do you have any domain restrictions on your GCP Account? Confirm domain pgcp.cloud is
        allowed.
        <br />
        <br />
        Here is how to check it:
      </Typography>
      <Typography className='mt-2' variant='body-regular'>
        1. ...
        <br />
        2. ...
      </Typography>
    </div>
  )
}

const SAPermissionsStep = () => {
  const { gcpSpec } = useAddAccountWizardContext()

  const map = {
    ORG: GCP_CLOUD_ACCOUNT_ORG_PERMISSIONS,
    FOLDER: GCP_CLOUD_ACCOUNT_FOLDER_PERMISSIONS,
    PROJECT: GCP_CLOUD_ACCOUNT_PROJECT_PERMISSIONS
  }

  const permissions = map[gcpSpec.accountType]

  return (
    <div>
      <Typography variant='body-regular'>
        Use the following permission to create a new service account:
      </Typography>
      <div className='p-1 bg-gray-200 rounded my-2'>
        <Typography variant='bodySmall-regular'>
          {permissions.map((e) => (
            <>
              {e}
              <br />
            </>
          ))}
        </Typography>
      </div>
      {/* HIDDEN FOR NOW can be removed later */}
      {/* <Typography variant='body-regular'>
        Additionally, following API Permissions are also required at each project level you want to
        be scanned:
        <div className='p-1 bg-gray-200 rounded my-2'>
          <Typography variant='bodySmall-regular'>
            {GCP_API_REQUIRED_PERMS.map((e) => (
              <>
                {e}
                <br />
              </>
            ))}
          </Typography>
        </div>
      </Typography> */}
    </div>
  )
}

const AccountCredentialStep = () => {
  const {
    setGcpSpec,
    gcpSpec: { jsonCredentials }
  } = useAddAccountWizardContext()

  const setJsonCredentials = (creds) => {
    setGcpSpec({ jsonCredentials: creds })
  }

  const handlePaste = async () => {
    try {
      const clipText = await navigator.clipboard.readText()
      setJsonCredentials(clipText)
    } catch (error) {
      enqueueNotification('Failed to paste from clipboard!', error)
    }
  }

  const safeCreds = (() => {
    if (!jsonCredentials) return {}
    try {
      return JSON.parse(jsonCredentials)
    } catch (error) {
      return { error: 'Invalid JSON:' + jsonCredentials }
    }
  })()

  return (
    <div>
      <div className='mb-4 relative'>
        <TextInput
          data-testid={getHTMLDataTestID('clouds-gcp-add', 'input', 'json-creds')}
          label='Upload the service account key file (.json) or enter it directly in below section (*required)'
          className='mt-3'
          multiline
          rows={10}
          sx={{ width: '100%' }}
          value={jsonCredentials}
          onChange={(e) => setJsonCredentials(e.target.value)}
        />
        <div
          data-testid={getHTMLDataTestID('clouds-gcp-add', 'div', 'file-upload')}
          className='absolute top-10 right-2 flex gap-2'
        >
          <FileUpload
            onFileRead={setJsonCredentials}
            Component={(props) => (
              <IconButton title='Upload' icon={faUpload} variant='gray' {...props} />
            )}
          />
          <IconButton
            data-testid={getHTMLDataTestID('clouds-gcp-add', 'button', 'paste')}
            title='Paste'
            onClick={handlePaste}
            icon={faPaste}
            variant='gray'
          />
          <IconButton
            data-testid={getHTMLDataTestID('clouds-gcp-add', 'button', 'clear')}
            disabled={!jsonCredentials}
            title='Clear'
            onClick={() => setJsonCredentials('')}
            icon={faBroomWide}
            variant='gray'
          />
        </div>
      </div>
      <JSONPrettyView data={safeCreds} />
    </div>
  )
}

const AccountSummary = ({}) => {
  const { org, name, description, gcpSpec, isEditMode } = useAddAccountWizardContext()
  const isWFFedAccount = gcpSpec.cloudIDType === 'workforce-fed'

  const targetIconTypeMap = {
    FOLDER: 'GCP_GOOGLE_FOLDER',
    ORG: 'GCP_ORG',
    PROJECT: 'GCP_GOOGLE_PROJECT'
  }

  const labelMap = {
    FOLDER: 'Folder',
    ORG: 'Organization',
    PROJECT: 'Project'
  }

  const safeCreds = (() => {
    if (!gcpSpec.jsonCredentials) return {}
    try {
      return JSON.parse(gcpSpec.jsonCredentials)
    } catch (error) {
      return { error: 'Invalid JSON:' + gcpSpec.jsonCredentials }
    }
  })()

  return (
    <div className='w-[100%]'>
      <Typography variant='body-regular'>Summary of Account</Typography>
      <LabelContent title='Account Name' content={name} />
      <LabelContent title='Description' content={description} />
      <LabelContent
        title='Account Type'
        content={
          <div className='flex gap-1 items-center'>
            {/* @ts-ignore */}
            <TargetIcon type={targetIconTypeMap[gcpSpec.accountType]} />
            <Typography variant='body-regular'>{labelMap[gcpSpec.accountType]}</Typography>
          </div>
        }
      />
      <LabelContent
        title='Workforce Federation'
        content={
          isWFFedAccount ? (
            <Label text='Yes' variant='success' />
          ) : (
            <Label text='No' variant='gray' />
          )
        }
      />
      {isWFFedAccount && <LabelContent title='Provider ID' content={gcpSpec.primaryDomain} />}
      <Typography
        className='!text-[#A6ABB6] min-w-[193px] !font-semibold my-4'
        variant='body-semiBold'
      >
        SA Credentials
      </Typography>
      <JSONPrettyView data={safeCreds} autoHeight />
    </div>
  )
}

export const useGcpSteps = () => {
  const { gcpSpec, isEditMode } = useAddAccountWizardContext()

  const isWFFedAccount = true // gcpSpec.cloudIDType === 'workforce-fed'

  return [
    !isEditMode
      ? {
          label: 'Select Account Type',
          content: <GCPAccountTypeStep />
        }
      : null,
    isWFFedAccount
      ? {
          label: 'Workforce Federation Setup',
          content: <WFedStep />
        }
      : null,
    {
      label: 'SA Permissions',
      content: <SAPermissionsStep />
    },
    {
      label: 'Credentials Setup',
      content: <AccountCredentialStep />
    },
    {
      label: 'Summary',
      content: <AccountSummary />
    }
  ].filter((e) => e)
}
